import { delay } from 'redux-saga';
import { all, put, call, take, race, getContext, takeLatest } from 'redux-saga/effects';
import { List } from 'immutable';

import { Logger } from 'utils/Logger';

import { LRS_HEALTH_CHECK_POLLING_DELAY_IN_MS } from 'constants';

import {
  coursesRequest,
  LRS_HEALTH_CHECK_FAILURE,
  START_LRS_HEALTH_CHECK_POLLING,
  STOP_LRS_HEALTH_CHECK_POLLING,
  REQUEST_LESSON_URL,
} from './types';

import {
  requestCoursesSuccess,
  requestCoursesFailure,
  lrsHealthCheckSuccess,
  lrsHealthCheckFailure,
} from './actions';

import playbookContentSaga from './playbook/sagas';
import { Analytics } from '../../utils/Analytics';
import Course from '../../models/CourseModel';

const logger = new Logger('Saga:Learning');

export function* requestCoursesSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.learning.courses'));

    Analytics.setCoursesInfo(List(response.data?.map((course) => Course.deserialize(course))));
    yield put(requestCoursesSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request funding searches activities with the following error', error);
    yield put(requestCoursesFailure(error));
  }
}

export function* healthCheckCoursesLauncherPolling(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  while (true) {
    try {
      yield call(
        authApiService.get,
        routerService.generate('api.learning.lrs_health_check', {
          lessonId: action.lessonId,
          token: action.lessonToken,
        }),
      );

      yield put(lrsHealthCheckSuccess());
    } catch (error) {
      logger.error('LRS Health Check Failed', error);
      yield put(lrsHealthCheckFailure());
    }

    yield call(delay, LRS_HEALTH_CHECK_POLLING_DELAY_IN_MS);
  }
}

export function* startLRSHealthCheckPollingSaga(action) {
  logger.info('Starting LRS health check polling');

  yield race({
    polling: call(healthCheckCoursesLauncherPolling, action),
    canceled: take(STOP_LRS_HEALTH_CHECK_POLLING),
    failed: take(LRS_HEALTH_CHECK_FAILURE),
  });

  logger.info('Stopped polling LRS health check.');
}

export function* requestLessonUrl(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.learning.launch_lesson', { lessonId: action.lessonId }),
    );

    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error('Failed to request lesson url with the following error', error);
    action.promise && action.promise.reject(error);
  }
}

export default function* learningSaga() {
  return yield all([
    takeLatest(coursesRequest.request, requestCoursesSaga),
    takeLatest(START_LRS_HEALTH_CHECK_POLLING, startLRSHealthCheckPollingSaga),
    takeLatest(REQUEST_LESSON_URL, requestLessonUrl),
    playbookContentSaga(),
  ]);
}
